/**
 * Single source of truth for supported languages.
 *
 * Key = public language code accepted in `?lng=` (kept short and stable because external
 * links / ads use them). `folder` = directory under `app/locales/` (BCP-47 `xx-YY`, `en` unregioned).
 *
 * Any of these forms resolve to the same key: `de`, `de-DE`, `de_de`, `DE-de`, `de-AT` (→ `de`).
 * Mirrors tpdrops-front `app/locales/languageMapping.ts`.
 */

export interface LanguageMappingEntry {
	/** Directory under app/locales/ holding translation.json */
	folder: string;
	name: string;
	nativeName: string;
	/** ISO 639-1 code sent to the backend when the public key is not one (e.g. `gr` → `el`). */
	backendCode?: string;
}

export interface LanguageMapping {
	[key: string]: LanguageMappingEntry;
}

export const DEFAULT_LANGUAGE = 'en';

export const languageMapping: LanguageMapping = {
	en: { folder: 'en', name: 'English', nativeName: 'English' },
	ar: { folder: 'ar-SA', name: 'Arabic', nativeName: 'العربية' },
	de: { folder: 'de-DE', name: 'German', nativeName: 'Deutsch' },
	gr: { folder: 'el-GR', name: 'Greek', nativeName: 'Ελληνικά', backendCode: 'el' },
	es: { folder: 'es-ES', name: 'Spanish', nativeName: 'Español' },
	// Public code `es-LA` as in tpdrops; folder is the UN M.49 region (`?lng=es-419` resolves via the folder alias).
	'es-LA': { folder: 'es-419', name: 'Spanish (Latin America)', nativeName: 'Español (Latinoamérica)' },
	fr: { folder: 'fr-FR', name: 'French', nativeName: 'Français' },
	he: { folder: 'he-IL', name: 'Hebrew', nativeName: 'עברית' },
	it: { folder: 'it-IT', name: 'Italian', nativeName: 'Italiano' },
	pl: { folder: 'pl-PL', name: 'Polish', nativeName: 'Polski' },
	pt: { folder: 'pt-PT', name: 'Portuguese', nativeName: 'Português' },
	tr: { folder: 'tr-TR', name: 'Turkish', nativeName: 'Türkçe' },
};

/** Alternate spellings → key: folder (`el-GR` → `gr`) and backendCode (`el` → `gr`). */
const aliasToKey: Record<string, string> = Object.fromEntries(
	Object.entries(languageMapping).flatMap(([key, entry]) => {
		const aliases: [string, string][] = [[entry.folder, key]];
		if (entry.backendCode) {
			aliases.push([entry.backendCode, key]);
		}
		return aliases;
	}),
);

/**
 * `pt_br` / `PT-br` / `pt-BR` → `pt-BR`; `de` → `de`; numeric regions (`es-419`) are kept as-is.
 * Returns '' for empty / non-string input.
 */
export const canonicalizeLanguageCode = (input: unknown): string => {
	const raw = Array.isArray(input) ? input[0] : input;
	if (typeof raw !== 'string' || !raw.trim()) {
		return '';
	}
	const [language, region] = raw.trim().replace(/_/g, '-').split('-');
	const lang = (language || '').toLowerCase();
	if (!lang) {
		return '';
	}
	if (!region) {
		return lang;
	}
	const upperRegion = /^[0-9]+$/.test(region) ? region : region.toUpperCase();
	return `${lang}-${upperRegion}`;
};

/** Bare language part: `he-IL` / `he_il` / `HE` → `he`. */
export const getLanguageCode = (lng?: string | null): string => canonicalizeLanguageCode(lng).split('-')[0] || '';

/**
 * Resolve any incoming code to a `languageMapping` key, or fall back to DEFAULT_LANGUAGE.
 * Order: exact key → folder / backendCode alias (`de-DE` → `de`, `el-GR` / `el` → `gr`)
 *        → base language (`pt-BR` → `pt`) → its alias → default.
 */
export const resolveLanguageCode = (lng: unknown): string => {
	const canonical = canonicalizeLanguageCode(lng);
	if (!canonical) {
		return DEFAULT_LANGUAGE;
	}
	const base = canonical.split('-')[0];
	return (
		(languageMapping[canonical] && canonical) ||
		aliasToKey[canonical] ||
		(languageMapping[base] && base) ||
		aliasToKey[base] ||
		DEFAULT_LANGUAGE
	);
};

/**
 * Value for `<html lang>`: base language of the resolved key, as in tpdrops
 * (`es-LA` / `es-419` → `es`, `de-DE` → `de`, unknown → `en`).
 */
export const getDocumentLanguage = (lng: unknown): string => getLanguageCode(resolveLanguageCode(lng)) || DEFAULT_LANGUAGE;

/** True when the code (in any spelling) maps to a known language. Unknown → false. */
export const isSupportedLanguage = (lng: unknown): boolean => {
	const canonical = canonicalizeLanguageCode(lng);
	if (!canonical) {
		return false;
	}
	const base = canonical.split('-')[0];
	return Boolean(languageMapping[canonical] || aliasToKey[canonical] || languageMapping[base] || aliasToKey[base]);
};

/** Directory under app/locales/ for the given code. Always returns an existing folder. */
export const getLanguageFolder = (lng: unknown): string => languageMapping[resolveLanguageCode(lng)].folder;

/** 2-letter ISO 639-1 code for the backend (`gr` → `el`, `de-DE` → `de`). */
export const getBackendLanguageCode = (lng: unknown): string => {
	const resolved = resolveLanguageCode(lng);
	return languageMapping[resolved].backendCode || resolved.split('-')[0] || DEFAULT_LANGUAGE;
};

/**
 * BCP-47 locale for Intl / autosuggest APIs, derived from the translation folder
 * (`de` → `de-DE`, `gr` → `el-GR`). English stays `en-US` for parity with previous Kayak defaults.
 */
export const getBcp47Locale = (lng: unknown): string => {
	const folder = getLanguageFolder(lng);
	return folder === 'en' ? 'en-US' : folder;
};

/**
 * Every spelling i18next should accept via `supportedLngs`:
 * keys, folder names, backend codes, plus dash/underscore lower-case aliases (`de-de`, `de_de`).
 */
export const getSupportedLanguages = (): string[] => {
	const codes = new Set<string>();
	Object.entries(languageMapping).forEach(([key, entry]) => {
		[key, entry.folder, entry.backendCode || key].forEach((code) => {
			codes.add(code);
			if (code.includes('-')) {
				codes.add(code.toLowerCase());
				codes.add(code.replace(/-/g, '_'));
				codes.add(code.replace(/-/g, '_').toLowerCase());
			}
		});
	});
	return Array.from(codes);
};
